/**
 * Join Table Modal
 * Allows user to join a table by selecting a buy-in amount
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GameTable } from '../../services/gameService';
import { showToast } from '../../lib/toast';

interface JoinTableModalProps {
  table: GameTable;
  userChips: number;
  onJoin: (buyIn: number) => Promise<void>;
  onClose: () => void;
}

const JoinTableModal: React.FC<JoinTableModalProps> = ({
  table,
  userChips,
  onJoin,
  onClose,
}) => {
  const [buyIn, setBuyIn] = useState(table.minBuyIn);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleJoin = async () => {
    setError(null);

    // Validation
    if (buyIn < table.minBuyIn) {
      setError(`Buy-in must be at least ${formatChips(table.minBuyIn)}`);
      return;
    }
    if (buyIn > table.maxBuyIn) {
      setError(`Buy-in cannot exceed ${formatChips(table.maxBuyIn)}`);
      return;
    }
    if (buyIn > userChips) {
      setError('Insufficient chips');
      return;
    }

    setIsJoining(true);
    try {
      await onJoin(buyIn);
      showToast(`Joined ${table.name} with ${formatChips(buyIn)}`, 'success');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to join table');
    } finally {
      setIsJoining(false);
    }
  };

  const formatChips = (chips: number): string => {
    if (chips >= 1000000) return `${(chips / 1000000).toFixed(1)}M`;
    if (chips >= 1000) return `${(chips / 1000).toFixed(1)}K`;
    return chips.toString();
  };

  const maxAllowedBuyIn = Math.min(table.maxBuyIn, userChips);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm">
      <motion.div
        className="w-full max-w-md bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl shadow-2xl border-2 border-yellow-600 overflow-hidden"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 bg-gradient-to-r from-yellow-600 to-orange-600">
          <h2 className="text-lg sm:text-xl font-bold text-white">Join Table</h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Table info */}
          <div className="p-3 sm:p-4 bg-gray-900/50 rounded-lg border border-gray-700">
            <h3 className="text-base sm:text-lg font-bold text-white mb-2">{table.name}</h3>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              <div>
                <span className="text-gray-400">Ante:</span>
                <span className="ml-1 sm:ml-2 text-white font-semibold">
                  {table.anteAmount}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Players:</span>
                <span className="ml-1 sm:ml-2 text-white font-semibold">
                  {table.currentPlayers}/{table.maxPlayers}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Min Buy-in:</span>
                <span className="ml-1 sm:ml-2 text-yellow-400 font-semibold">
                  {formatChips(table.minBuyIn)}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Max Buy-in:</span>
                <span className="ml-1 sm:ml-2 text-yellow-400 font-semibold">
                  {formatChips(table.maxBuyIn)}
                </span>
              </div>
            </div>
          </div>

          {/* Your chips */}
          <div className="text-center p-3 bg-gray-900/30 rounded-lg border border-gray-700">
            <div className="text-xs sm:text-sm text-gray-400 mb-1">Your Available Chips</div>
            <div className="text-xl sm:text-2xl font-bold text-yellow-400">{formatChips(userChips)}</div>
          </div>

          {/* Buy-in selector */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
              Buy-in Amount: <span className="text-yellow-400 font-bold text-base sm:text-lg">{formatChips(buyIn)}</span>
            </label>
            <input
              type="range"
              min={table.minBuyIn}
              max={maxAllowedBuyIn}
              value={buyIn}
              onChange={(e) => setBuyIn(Number(e.target.value))}
              disabled={isJoining}
              className="w-full h-2 sm:h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500 touch-manipulation"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Min: {formatChips(table.minBuyIn)}</span>
              <span>Max: {formatChips(maxAllowedBuyIn)}</span>
            </div>

            {/* Quick select buttons */}
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mt-2 sm:mt-3">
              <button
                onClick={() => setBuyIn(table.minBuyIn)}
                disabled={isJoining}
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-600 disabled:opacity-50 text-white text-xs sm:text-sm rounded transition-colors touch-manipulation"
              >
                Min
              </button>
              <button
                onClick={() => setBuyIn(Math.floor((table.minBuyIn + maxAllowedBuyIn) / 2))}
                disabled={isJoining}
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-600 disabled:opacity-50 text-white text-xs sm:text-sm rounded transition-colors touch-manipulation"
              >
                Mid
              </button>
              <button
                onClick={() => setBuyIn(maxAllowedBuyIn)}
                disabled={isJoining}
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-600 disabled:opacity-50 text-white text-xs sm:text-sm rounded transition-colors touch-manipulation"
              >
                Max
              </button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-2 sm:p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-xs sm:text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={onClose}
              disabled={isJoining}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 active:bg-gray-600 disabled:opacity-50 text-white text-sm rounded-lg font-semibold transition-colors touch-manipulation"
            >
              Cancel
            </button>
            <button
              onClick={handleJoin}
              disabled={isJoining}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 active:from-green-500 active:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg font-semibold transition-all shadow-lg touch-manipulation"
            >
              {isJoining ? 'Joining...' : `Join (${formatChips(buyIn)})`}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default JoinTableModal;
